<template>
  <modal
    :show="isVisible"
    :title="t('Vender combo')"
    elem-id="modal_pdv_vender_combo"
    size="small"
    :close-label="t('Cancelar')"
    :save-label="isSaving ? t('Salvando') : t('Adicionar à OS')"
    :save-fn="handleVender"
    :save-disabled-fn="() => isSaving || combos.length === 0"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <div v-if="carregado && combos.length === 0" class="vc-aviso" data-testid="pdv-vender-combo-vazio">
        {{ t('Esta unidade não vende nenhum combo: cadastre o preço em Configurações, Preço dos combos.') }}
      </div>

      <template v-else>
        <select-list
          elem-id="pdvVenderComboId"
          data-testid="pdv-vender-combo-select"
          v-model="comboId"
          :itens-select="opcoes"
          :label="t('Combo')"
          :placeholder="t('Selecione o combo')"
          :auto-select-first="false"
        />
        <small v-if="erroCampo" class="error-message">{{ erroCampo }}</small>

        <dl v-if="selecionado" class="vc-resumo" data-testid="pdv-vender-combo-resumo">
          <div>
            <dt>{{ t('Cobre') }}</dt>
            <dd>{{ selecionado.so_service_name }} — {{ selecionado.so_priority_name }}</dd>
          </div>
          <div>
            <dt>{{ t('Pares') }}</dt>
            <dd>{{ selecionado.so_combo_quantity }}</dd>
          </div>
          <div>
            <dt>{{ t('Validade') }}</dt>
            <dd>{{ selecionado.so_combo_validity_days ? `${selecionado.so_combo_validity_days} ${t('dias')}` : t('Não vence') }}</dd>
          </div>
          <div>
            <dt>{{ t('Preço') }}</dt>
            <dd data-testid="pdv-vender-combo-preco">{{ formatarMoeda(selecionado.so_combo_price_price ?? 0, franchiseId) }}</dd>
          </div>
        </dl>
      </template>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { error as logError } from '../../utils/DebugLogger';
import { useSOCheckout } from '../../composables/useSOCheckout';
import { useSOComboPrices } from '../../composables/useSOComboPrices';
import { useMoedaFranquia } from '../../composables/useMoedaFranquia';
import { emitValidationFailed } from '../../composables/useFormValidation';
import type { ComboPrices_PriceRow } from '../../contracts/ComboPrices_PriceRow';

interface OpenPayload {
  orderId: number | string;
  franchiseId: number | string;
}

const { t } = useI18n();
const { addItem } = useSOCheckout();
const { fetchSellable } = useSOComboPrices();
const { formatarMoeda } = useMoedaFranquia();

const isVisible = ref<boolean>(false);
const isSaving = ref<boolean>(false);
const carregado = ref<boolean>(false);
const orderId = ref<number | string | null>(null);
const franchiseId = ref<number | string | null>(null);
const combos = ref<ComboPrices_PriceRow[]>([]);
const comboId = ref<number | string | undefined>(undefined);
const erroCampo = ref<string>('');

const opcoes = computed(() => combos.value.map((c) => ({
  ID: c.so_combo_price_combo_id ?? '',
  Name: c.so_combo_name ?? '',
})));

const selecionado = computed(() =>
  combos.value.find((c) => c.so_combo_price_combo_id === Number(comboId.value)) ?? null);

const handleClose = (): void => {
  isVisible.value = false;
  isSaving.value = false;
  carregado.value = false;
  combos.value = [];
  comboId.value = undefined;
  erroCampo.value = '';
};

const handleVender = async (): Promise<void> => {
  if (!comboId.value) {
    erroCampo.value = t('Campo obrigatório');
    emitValidationFailed('PDVVenderComboModal', { combo_id: erroCampo.value });
    return;
  }

  isSaving.value = true;
  try {
    await addItem(orderId.value, { type: 'combo', combo_id: Number(comboId.value) });
    eventBus.emit('globalMessage.success', t('Combo adicionado à OS'));
    eventBus.emit('pdv.item-added');
    handleClose();
  } catch (err) {
    // A recusa do backend já virou toast no useSOCheckout.addItem.
    logError('PDVVenderComboModal', 'handleVender', 'Error', err);
  } finally {
    isSaving.value = false;
  }
};

const handleModalOpen = async (data?: OpenPayload): Promise<void> => {
  if (!data) return;
  orderId.value = data.orderId;
  franchiseId.value = data.franchiseId;
  isVisible.value = true;
  try {
    combos.value = await fetchSellable(Number(data.franchiseId));
  } catch (err) {
    logError('PDVVenderComboModal', 'handleModalOpen', 'Error', err);
    const message = err instanceof Error ? err.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao carregar os combos da unidade') + ': ' + message);
  } finally {
    carregado.value = true;
  }
};

onMounted(() => {
  eventBus.on<OpenPayload>('modal.pdv-vender-combo.open', handleModalOpen);
});

onBeforeUnmount(() => {
  eventBus.off<OpenPayload>('modal.pdv-vender-combo.open', handleModalOpen);
});
</script>

<style scoped>
.vc-aviso {
  padding: 1rem;
}

.vc-resumo {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0 0;
}

.vc-resumo dt {
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
}

.vc-resumo dd {
  margin: 0;
  font-weight: 600;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
}
</style>
